<template>
  <form @submit.prevent="saveOrganization" ref="organizationEditForm">
    <div class="modal" tabindex="-1" role="dialog" backdrop="static" id="editOrganizationModal" @keyup.esc="onEsc($event)">
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header header">
            <h3 class="modal-title"><span v-if="action === 'add'">{{ $t('editOrganizationModal.titleAdd') }}</span><span v-if="action === 'edit'">{{ $t('editOrganizationModal.titleEdit') }}</span></h3>
            <button type="button" class="btn-close btn-close-white" @click="close" aria-label="Close">

            </button>
          </div>
          <div class="modal-body">
            <div class="form">
              <div v-show="errorMessage" class="alert alert-danger failed">{{ errorMessage }}</div>
              <div class="form-group">
                <label for="vendorInput">{{ $t('editOrganizationModal.nameInput') }}</label>
                <input type="text" class="form-control" :class="{ 'is-invalid': v$.organization.name.$dirty && v$.organization.name.$error }" id="nameInput" v-model.trim="organization.name" :placeholder="$t('editOrganizationModal.namePlaceholder')" maxlength="50" />
                <div class="invalid-feedback" v-if="v$.organization.name.$dirty">
                  <div class="error" v-if="!v$.organization.name.required">{{ $t('editOrganizationModal.nameRequired') }}</div>
                  <div class="error" v-if="!v$.organization.name.minLength">{{ $t('editOrganizationModal.nameMinLength', { min: v$.organization.name.$params.minLength.min }) }}</div>
                  <div class="error" v-if="!v$.organization.name.maxLength">{{ $t('editOrganizationModal.nameMaxLength', { max: v$.organization.name.$params.maxLength.max }) }}</div>
                </div>
              </div>
              <div class="form-group">
                <label for="codeInput">{{ $t('editOrganizationModal.codeInput') }}</label>
                <input type="text" class="form-control" :class="{ 'is-invalid': v$.organization.code.$dirty && v$.organization.code.$error }" id="codeInput" v-model.trim="organization.code" :placeholder="$t('editOrganizationModal.codePlaceholder')" maxlength="50" />
                <div class="invalid-feedback" v-if="v$.organization.code.$dirty">
                  <div class="error" v-if="!v$.organization.code.required">{{ $t('editOrganizationModal.codeRequired') }}</div>
                  <div class="error" v-if="!v$.organization.code.minLength">{{ $t('editOrganizationModal.codeMinLength', { min: v$.organization.code.$params.minLength.min }) }}</div>
                  <div class="error" v-if="!v$.organization.code.maxLength">{{ $t('editOrganizationModal.codeMaxLength', { max: v$.organization.code.$params.maxLength.max }) }}</div>
                </div>
              </div>
            </div>

            <!-- Plan administration (admin + edit only) -->
            <template v-if="action === 'edit' && isAdmin">
              <hr />
              <div class="plan-section">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h6 class="mb-0">{{ $t('editOrganizationModal.planTitle') }}</h6>
                  <span v-if="planLoading" class="text-muted small">{{ $t('editOrganizationModal.planLoading') }}</span>
                </div>

                <div v-show="planError" class="alert alert-danger py-1 small">{{ planError }}</div>
                <div v-show="planSuccess" class="alert alert-success py-1 small">{{ planSuccess }}</div>

                <!-- No plan yet -->
                <div v-if="!planLoading && !plan && !showPlanForm" class="text-center py-2">
                  <p class="text-muted small mb-2">{{ $t('editOrganizationModal.noPlan') }}</p>
                  <button type="button" class="btn btn-sm btn-outline-primary" @click="initNewPlan">
                    {{ $t('editOrganizationModal.planCreate') }}
                  </button>
                </div>

                <!-- Plan form (create or edit) -->
                <div v-if="!planLoading && (plan || showPlanForm)" class="plan-form">
                  <div class="row g-2">
                    <div class="col-md-4">
                      <label class="form-label small">{{ $t('editOrganizationModal.planType') }}</label>
                      <select class="form-select form-select-sm" v-model="planForm.plan">
                        <option value="BASIC">Basic</option>
                        <option value="PRO">Pro</option>
                        <option value="ENTERPRISE">Enterprise</option>
                        <option value="LEGACY">Legacy</option>
                      </select>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label small">{{ $t('editOrganizationModal.planStatus') }}</label>
                      <select class="form-select form-select-sm" v-model="planForm.status">
                        <option value="ACTIVE">Aktiv</option>
                        <option value="TRIAL">Trial</option>
                        <option value="CANCELLED">Gekündigt</option>
                        <option value="SUSPENDED">Gesperrt</option>
                      </select>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label small">{{ $t('editOrganizationModal.planBillingCycle') }}</label>
                      <select class="form-select form-select-sm" v-model="planForm.billingCycle">
                        <option value="MONTHLY">Monatlich</option>
                        <option value="ANNUAL">Jährlich</option>
                      </select>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label small">{{ $t('editOrganizationModal.planActiveFrom') }}</label>
                      <input type="date" class="form-control form-control-sm" v-model="planForm.activeFrom" />
                    </div>
                    <div class="col-md-4">
                      <label class="form-label small">{{ $t('editOrganizationModal.planActiveUntil') }}</label>
                      <input type="date" class="form-control form-control-sm" v-model="planForm.activeUntil" />
                    </div>
                    <div class="col-md-4">
                      <label class="form-label small">{{ $t('editOrganizationModal.planBasePrice') }} (€)</label>
                      <input type="number" step="0.01" class="form-control form-control-sm" v-model="planForm.basePrice" />
                    </div>
                    <div class="col-md-3">
                      <label class="form-label small">{{ $t('editOrganizationModal.planSensorLimit') }}</label>
                      <input type="number" class="form-control form-control-sm" v-model="planForm.sensorLimit" :placeholder="$t('editOrganizationModal.planUnlimited')" />
                    </div>
                    <div class="col-md-3">
                      <label class="form-label small">{{ $t('editOrganizationModal.planUserLimit') }}</label>
                      <input type="number" class="form-control form-control-sm" v-model="planForm.userLimit" :placeholder="$t('editOrganizationModal.planUnlimited')" />
                    </div>
                    <div class="col-md-6">
                      <label class="form-label small">{{ $t('editOrganizationModal.planBillingEmail') }}</label>
                      <input type="email" class="form-control form-control-sm" v-model="planForm.billingEmail" />
                    </div>
                    <div class="col-12">
                      <label class="form-label small">{{ $t('editOrganizationModal.planNotes') }}</label>
                      <textarea class="form-control form-control-sm" rows="2" v-model="planForm.notes"></textarea>
                    </div>
                  </div>
                  <div class="d-flex gap-2 mt-2">
                    <button type="button" class="btn btn-sm btn-primary" @click="savePlan" :disabled="planSaving">
                      {{ plan ? $t('editOrganizationModal.planSave') : $t('editOrganizationModal.planCreate') }}
                    </button>
                    <button v-if="plan" type="button" class="btn btn-sm btn-outline-danger" @click="cancelPlan" :disabled="planSaving">
                      {{ $t('editOrganizationModal.planCancel') }}
                    </button>
                    <button v-if="!plan" type="button" class="btn btn-sm btn-secondary" @click="showPlanForm = false">
                      {{ $t('editOrganizationModal.close') }}
                    </button>
                  </div>
                </div>

                <!-- Add-ons (only when a plan exists) -->
                <template v-if="!planLoading && plan">
                  <div class="d-flex justify-content-between align-items-center mt-3 mb-1">
                    <h6 class="mb-0 small fw-semibold">{{ $t('editOrganizationModal.addonsTitle') }}</h6>
                    <button type="button" class="btn btn-sm btn-outline-secondary" @click="showAddonForm = !showAddonForm">
                      {{ showAddonForm ? $t('editOrganizationModal.addonFormClose') : $t('editOrganizationModal.addonAdd') }}
                    </button>
                  </div>

                  <!-- Addon form -->
                  <div v-if="showAddonForm" class="addon-form border rounded p-2 mb-2 bg-light">
                    <div class="row g-2">
                      <div class="col-md-4">
                        <label class="form-label small">{{ $t('editOrganizationModal.addonType') }}</label>
                        <select class="form-select form-select-sm" v-model="addonForm.addonType">
                          <option value="CO2_REPORT">CO2-Bericht</option>
                          <option value="API_ACCESS">API-Zugang</option>
                          <option value="EXTRA_USER">Extra Benutzer</option>
                          <option value="CUSTOM_REPORT">Individueller Bericht</option>
                          <option value="CONTROLLER">Controller</option>
                          <option value="HW_SENSOR_RENTED">HW-Sensor (Miete)</option>
                          <option value="HW_SENSOR_OWNED">HW-Sensor (Kauf)</option>
                          <option value="MANUAL_SENSOR">Manueller Sensor</option>
                          <option value="LTE">LTE-Modem</option>
                          <option value="SETUP_FEE">Einrichtungsgebühr</option>
                          <option value="SIM_CARD">SIM-Karte</option>
                          <option value="OTHER">Sonstiges</option>
                        </select>
                      </div>
                      <div class="col-md-4">
                        <label class="form-label small">{{ $t('editOrganizationModal.addonChargeType') }}</label>
                        <select class="form-select form-select-sm" v-model="addonForm.chargeType">
                          <option value="MONTHLY">Monatlich</option>
                          <option value="ONE_TIME">Einmalig</option>
                          <option value="PER_USE">Pro Nutzung</option>
                        </select>
                      </div>
                      <div class="col-md-2">
                        <label class="form-label small">{{ $t('editOrganizationModal.addonQuantity') }}</label>
                        <input type="number" min="1" class="form-control form-control-sm" v-model="addonForm.quantity" />
                      </div>
                      <div class="col-md-2">
                        <label class="form-label small">{{ $t('editOrganizationModal.addonUnitPrice') }} (€)</label>
                        <input type="number" step="0.01" class="form-control form-control-sm" v-model="addonForm.unitPrice" />
                      </div>
                      <div class="col-md-3">
                        <label class="form-label small">{{ $t('editOrganizationModal.planActiveFrom') }}</label>
                        <input type="date" class="form-control form-control-sm" v-model="addonForm.activeFrom" />
                      </div>
                      <div class="col-md-3">
                        <label class="form-label small">{{ $t('editOrganizationModal.planActiveUntil') }}</label>
                        <input type="date" class="form-control form-control-sm" v-model="addonForm.activeUntil" />
                      </div>
                      <div class="col-md-6">
                        <label class="form-label small">{{ $t('editOrganizationModal.addonDescription') }}</label>
                        <input type="text" class="form-control form-control-sm" v-model="addonForm.description" maxlength="255" />
                      </div>
                    </div>
                    <button type="button" class="btn btn-sm btn-primary mt-2" @click="saveAddon" :disabled="addonSaving">
                      {{ $t('editOrganizationModal.addonSave') }}
                    </button>
                  </div>

                  <!-- Addons list -->
                  <table v-if="plan.addons && plan.addons.length" class="table table-sm table-striped mb-0">
                    <thead>
                      <tr>
                        <th class="small">{{ $t('editOrganizationModal.addonType') }}</th>
                        <th class="small">{{ $t('editOrganizationModal.addonChargeType') }}</th>
                        <th class="small text-end">{{ $t('editOrganizationModal.addonQuantity') }}</th>
                        <th class="small text-end">{{ $t('editOrganizationModal.addonUnitPrice') }}</th>
                        <th class="small">{{ $t('editOrganizationModal.planActiveFrom') }}</th>
                        <th class="small">{{ $t('editOrganizationModal.planActiveUntil') }}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="addon in plan.addons" :key="addon.id">
                        <td class="small">{{ addonTypeLabel(addon.addonType) }}</td>
                        <td class="small">{{ chargeTypeLabel(addon.chargeType) }}</td>
                        <td class="small text-end">{{ addon.quantity }}</td>
                        <td class="small text-end">{{ formatPrice(addon.unitPrice) }} €</td>
                        <td class="small">{{ formatDate(addon.activeFrom) }}</td>
                        <td class="small">{{ addon.activeUntil ? formatDate(addon.activeUntil) : '–' }}</td>
                        <td class="text-end">
                          <button type="button" class="btn btn-xs btn-outline-danger py-0 px-1" @click="deleteAddon(addon.id)">
                            <font-awesome-icon icon="trash" />
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <p v-else class="text-muted small mb-0">{{ $t('editOrganizationModal.noAddons') }}</p>
                </template>
              </div>
            </template>
          </div>
          <div class="modal-footer">
            <button type="submit" class="btn btn-primary"><span v-if="action === 'add'">{{ $t('editOrganizationModal.buttonAdd') }}</span><span v-if="action === 'edit'">{{ $t('editOrganizationModal.buttonSave') }}</span></button>
            <button type="button" class="btn btn-secondary" @click="close">{{ $t('editOrganizationModal.close') }}</button>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { Modal } from 'bootstrap'
import { required, minLength, maxLength } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import organizationService from '@/services/organizations'
import ModalViewMixin from '@/mixins/ModalViewMixin.js'

export default {
  setup () { return { v$: useVuelidate() } },
  name: 'EditOrganizationModal',
  mixins: [ModalViewMixin],
  props: {
    selectedOrganization: {
      type: Object,
      required: true
    }
  },
  data () {
    const today = new Date().toISOString().slice(0, 10)
    return {
      organization: {},
      errorMessage: '',
      // Plan
      plan: null,
      planLoading: false,
      planSaving: false,
      showPlanForm: false,
      planError: '',
      planSuccess: '',
      planForm: { plan: 'BASIC', status: 'ACTIVE', billingCycle: 'MONTHLY', activeFrom: today, activeUntil: '', sensorLimit: null, userLimit: null, basePrice: 0, billingEmail: '', notes: '' },
      // Addons
      showAddonForm: false,
      addonSaving: false,
      addonForm: { addonType: 'CO2_REPORT', chargeType: 'MONTHLY', quantity: 1, unitPrice: 0, activeFrom: today, activeUntil: '', description: '' }
    }
  },
  computed: {
    isAdmin () {
      return this.$store.getters.client && this.$store.getters.client.role === 4
    }
  },
  watch: {
    modalOpened () {
      this.copyOrganization()
      this.triggerFocus()
      if (this.action === 'edit' && this.isAdmin && this.selectedOrganization.id) {
        this.loadPlan()
      } else {
        this.resetPlanState()
      }
    }
  },
  validations: {
    organization: {
      name: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(50)
      },
      code: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(50)
      }
    }
  },
  mounted () {
    document.getElementById('editOrganizationModal').addEventListener('shown.bs.modal', () => {
      this.triggerFocus()
    })
  },
  methods: {
    emptyPlanForm () {
      return {
        plan: 'BASIC',
        status: 'ACTIVE',
        billingCycle: 'MONTHLY',
        activeFrom: new Date().toISOString().slice(0, 10),
        activeUntil: '',
        sensorLimit: null,
        userLimit: null,
        basePrice: 0,
        billingEmail: '',
        notes: ''
      }
    },
    emptyAddonForm () {
      return {
        addonType: 'CO2_REPORT',
        chargeType: 'MONTHLY',
        quantity: 1,
        unitPrice: 0,
        activeFrom: new Date().toISOString().slice(0, 10),
        activeUntil: '',
        description: ''
      }
    },
    resetPlanState () {
      this.plan = null
      this.planLoading = false
      this.planSaving = false
      this.showPlanForm = false
      this.planError = ''
      this.planSuccess = ''
      this.planForm = this.emptyPlanForm()
      this.showAddonForm = false
      this.addonSaving = false
      this.addonForm = this.emptyAddonForm()
    },
    loadPlan () {
      this.planLoading = true
      this.planError = ''
      this.planSuccess = ''
      organizationService.getOrganizationPlan(this.selectedOrganization.id).then(data => {
        this.plan = data.plan || null
        if (this.plan) {
          this.planForm = {
            plan: this.plan.plan,
            status: this.plan.status,
            billingCycle: this.plan.billingCycle,
            activeFrom: this.toDateString(this.plan.activeFrom),
            activeUntil: this.toDateString(this.plan.activeUntil),
            sensorLimit: this.plan.sensorLimit,
            userLimit: this.plan.userLimit,
            basePrice: this.plan.basePrice,
            billingEmail: this.plan.billingEmail || '',
            notes: this.plan.notes || ''
          }
        }
        this.planLoading = false
      }).catch(() => {
        this.planLoading = false
      })
    },
    initNewPlan () {
      this.planForm = this.emptyPlanForm()
      this.showPlanForm = true
    },
    savePlan () {
      this.planSaving = true
      this.planError = ''
      this.planSuccess = ''
      const payload = { ...this.planForm }
      const orgId = this.selectedOrganization.id
      const promise = this.plan
        ? organizationService.updateOrganizationPlan(orgId, this.plan.id, payload)
        : organizationService.createOrganizationPlan(orgId, payload)
      promise.then(data => {
        this.plan = data.plan
        this.showPlanForm = false
        this.planSuccess = this.$t('editOrganizationModal.planSaved')
        this.planSaving = false
        setTimeout(() => { this.planSuccess = '' }, 3000)
      }).catch(err => {
        this.planError = err.message || 'Fehler beim Sichern des Plans'
        this.planSaving = false
      })
    },
    cancelPlan () {
      if (!confirm(this.$t('editOrganizationModal.planCancelConfirm'))) return
      this.planSaving = true
      organizationService.cancelOrganizationPlan(this.selectedOrganization.id, this.plan.id).then(() => {
        this.plan.status = 'CANCELLED'
        this.planForm.status = 'CANCELLED'
        this.planSaving = false
        this.planSuccess = this.$t('editOrganizationModal.planCancelled')
        setTimeout(() => { this.planSuccess = '' }, 3000)
      }).catch(err => {
        this.planError = err.message || 'Fehler beim Kündigen'
        this.planSaving = false
      })
    },
    saveAddon () {
      this.addonSaving = true
      this.planError = ''
      organizationService.addOrganizationPlanAddon(this.selectedOrganization.id, this.plan.id, { ...this.addonForm }).then(data => {
        this.plan.addons.push(data.addon)
        this.addonForm = this.emptyAddonForm()
        this.showAddonForm = false
        this.addonSaving = false
        this.planSuccess = this.$t('editOrganizationModal.addonSaved')
        setTimeout(() => { this.planSuccess = '' }, 3000)
      }).catch(err => {
        this.planError = err.message || 'Fehler beim Hinzufügen des Add-Ons'
        this.addonSaving = false
      })
    },
    deleteAddon (addonId) {
      if (!confirm(this.$t('editOrganizationModal.addonDeleteConfirm'))) return
      organizationService.deleteOrganizationPlanAddon(this.selectedOrganization.id, this.plan.id, addonId).then(() => {
        this.plan.addons = this.plan.addons.filter(a => a.id !== addonId)
        this.planSuccess = this.$t('editOrganizationModal.addonDeleted')
        setTimeout(() => { this.planSuccess = '' }, 3000)
      }).catch(err => {
        this.planError = err.message || 'Fehler beim Löschen des Add-Ons'
      })
    },
    toDateString (val) {
      if (!val) return ''
      const d = new Date(val)
      return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
    },
    formatDate (val) {
      if (!val) return '–'
      return new Date(val).toLocaleDateString('de-DE')
    },
    formatPrice (val) {
      if (val == null) return '0,00'
      return Number(val).toFixed(2).replace('.', ',')
    },
    addonTypeLabel (type) {
      const labels = {
        CO2_REPORT: 'CO2-Bericht',
        API_ACCESS: 'API-Zugang',
        EXTRA_USER: 'Extra Benutzer',
        CUSTOM_REPORT: 'Ind. Bericht',
        CONTROLLER: 'Controller',
        HW_SENSOR_RENTED: 'HW-Sensor (Miete)',
        HW_SENSOR_OWNED: 'HW-Sensor (Kauf)',
        MANUAL_SENSOR: 'Man. Sensor',
        LTE: 'LTE-Modem',
        SETUP_FEE: 'Einrichtung',
        SIM_CARD: 'SIM-Karte',
        OTHER: 'Sonstiges'
      }
      return labels[type] || type
    },
    chargeTypeLabel (type) {
      const labels = { MONTHLY: 'Monatlich', ONE_TIME: 'Einmalig', PER_USE: 'Pro Nutzung' }
      return labels[type] || type
    },
    copyOrganization () {
      this.organization = Object.assign({}, this.selectedOrganization)
    },
    saveOrganization () {
      this.v$.$touch()
      if (this.v$.$invalid) {
        return
      }

      if (this.action === 'add') {
        organizationService.addOrganization(this.organization).then((createdOrganization) => {
          this.flagChanges()
        }).catch(error => {
          this.errorMessage = this.localizedBackendMessage(error.message)
        })
      } else if (this.action === 'edit') {
        organizationService.updateOrganization(this.organization.id, this.organization).then((updatedOrganization) => {
          this.flagChanges()
        }).catch(error => {
          this.errorMessage = this.localizedBackendMessage(error.message)
        })
      }
    },
    close () {
      this.v$.$reset()
      this.errorMessage = ''
      this.copyOrganization()
      this.emitClosed()
      Modal.getInstance(document.getElementById('editOrganizationModal'))?.hide()
    },
    triggerFocus () {
      document.getElementById('codeInput')?.focus()
      document.getElementById('nameInput')?.focus()
    }
  }
}
</script>

<style lang="scss" scoped>
.modal {
  .blue-text {
    color: #0069d9;
    font-weight: 700;
  }

  .td-right {
    text-align: right;
  }

  .plan-section {
    font-size: 13px;
  }

  .plan-form {
    background: #f8f9fa;
    border-radius: 6px;
    padding: 10px;
  }

  .addon-form {
    font-size: 12px;
  }

  .btn-xs {
    font-size: 11px;
    line-height: 1.2;
  }
}
</style>
